
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { documentStore } from "@/store/store";
import { findNode } from "@/lib/tree/tree";
import { Media } from "@/types/media";
import { TreeNode } from "@/types/tree";
import lodash from "lodash";
import draggable from "vuedraggable";

@Component({
  components: {
    draggable
  }
})
export default class GalleryStrip extends Vue {
  @Prop({ required: true })
  basePathToImageControl!: string;

  @Prop({ required: true })
  elementId!: TreeNode["id"];

  @Prop({ default: 0 })
  selectedImageIndex!: number;

  dragOptions = {
    animation: 200,
    forceFallback: true,
    filter: ".add-image-btn",
    ghostClass: "is-ghost-image"
  };

  updateGallery(newValue: Media[]) {
    const updateValue = newValue.map(image => ({ image, type: "image" }));

    documentStore.unthrottledPropertyChanged({
      elementId: this.elementId,
      path: this.repeaterBasePath,
      value: updateValue,
      meta: {
        snapshotLabel: "Reorder Gallery Images"
      }
    });
  }

  get repeaterBasePath() {
    return this.basePathToImageControl.split("[]")[0];
  }

  get repeaterSubPath() {
    const parts = this.basePathToImageControl.split("[]");
    return parts[1]?.startsWith(".") ? parts[1].substring(1) : parts[1];
  }

  get repeaterArrayValue() {
    const node = findNode(documentStore.document.tree, this.elementId);
    if (!node) return [];

    const result = lodash.get(node.data.properties, this.repeaterBasePath);
    return Array.isArray(result) ? result : [];
  }

  get galleryImages(): Media[] {
    // Extract images using the sub-path from repeater items
    return this.repeaterArrayValue
      .map(item => {
        if (this.repeaterSubPath) {
          return lodash.get(item, this.repeaterSubPath) || null;
        }
        return item;
      })
      .filter(Boolean);
  }

  get selectedIndex() {
    return Math.max(
      0,
      Math.min(this.selectedImageIndex, this.galleryImages.length - 1)
    );
  }

  get pathToImageControlWhenInGalleryMode() {
    return `${this.repeaterBasePath}[${this.selectedIndex}].${this.repeaterSubPath}`;
  }

  get pathToImageCaptionWhenInGalleryMode() {
    return `${this.repeaterBasePath}[${this.selectedIndex}].caption`;
  }

  @Watch("pathToImageControlWhenInGalleryMode", { immediate: true })
  emitPathChange() {
    this.$emit("path-selected", this.pathToImageControlWhenInGalleryMode);
    this.$emit(
      "caption-path-selected",
      this.pathToImageCaptionWhenInGalleryMode
    );
  }

  @Watch("selectedIndex")
  emitIndexChange() {
    this.$emit("index-changed", this.selectedIndex);
  }

  selectImage(index: number) {
    if (index >= 0 && index < this.galleryImages.length) {
      this.$emit("index-changed", index);
    }
  }

  addImage() {
    const newImage: Media = {
      id: -1,
      type: "external_image",
      url: "",
      alt: "",
      caption: ""
    };

    const newItem: any = {};
    if (this.repeaterSubPath) {
      lodash.set(newItem, this.repeaterSubPath, newImage);
    }
    // Add caption for image repeaters
    if (this.repeaterSubPath === "image") {
      newItem.caption = "";
    }

    const currentArray = [...this.repeaterArrayValue, newItem];
    const newIndex = currentArray.length - 1;

    documentStore.unthrottledPropertyChanged({
      elementId: this.elementId,
      path: this.repeaterBasePath,
      value: currentArray,
      meta: {
        snapshotLabel: "Add Image to Gallery"
      }
    });

    this.$emit("index-changed", newIndex);
  }

  deleteImage(index: number) {
    if (
      index < 0 ||
      index >= this.galleryImages.length ||
      this.galleryImages.length <= 1
    )
      return;

    const currentArray = [...this.repeaterArrayValue];
    currentArray.splice(index, 1);

    documentStore.unthrottledPropertyChanged({
      elementId: this.elementId,
      path: this.repeaterBasePath,
      value: currentArray,
      meta: {
        snapshotLabel: "Delete Image from Gallery"
      }
    });

    // Adjust selected index if necessary
    let newSelectedIndex = this.selectedIndex;
    if (index <= this.selectedIndex) {
      newSelectedIndex = Math.max(0, this.selectedIndex - 1);
    }
    if (newSelectedIndex >= currentArray.length) {
      newSelectedIndex = currentArray.length - 1;
    }

    this.$emit("index-changed", newSelectedIndex);
  }
}
